/**
 * Utility functions for notification API calls with retry logic and rate limiting
 */

// Rate limiting for API calls
const rateLimitMap = new Map();

/**
 * Check if an API call should be rate limited
 * @param {string} key - Unique key for the API call
 * @param {number} minInterval - Minimum interval between calls in milliseconds
 * @returns {boolean} - Whether the call should be rate limited
 */
export const shouldRateLimit = (key, minInterval = 1000) => {
  const now = Date.now();
  const lastCall = rateLimitMap.get(key);
  
  if (lastCall && (now - lastCall) < minInterval) {
    return true;
  }
  
  rateLimitMap.set(key, now);
  return false;
};

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @param {number} baseDelay - Base delay in milliseconds (default: 1000)
 * @returns {Promise} - Promise that resolves with the function result
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on 401 (Unauthorized) or 403 (Forbidden)
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw error;
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Debounce function to limit API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit API calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Clear rate limit for a specific key
 * @param {string} key - Key to clear
 */
export const clearRateLimit = (key) => {
  rateLimitMap.delete(key);
};

/**
 * Clear all rate limits
 */
export const clearAllRateLimits = () => {
  rateLimitMap.clear();
}; 